import React, { useState, useEffect } from 'react';
import IntlMessages from '@iso/components/utility/intlMessages';
import AssetModalWrapper from './AssetModal.styles';
import { Form, Input, Button, Select, Row, Col, DatePicker, Tabs, Card, Timeline, Popconfirm, InputNumber, Upload, Alert, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { activityService, assetService, propertyService, attachment as attachmentService } from '@iso/services';
import moment from 'moment';
import _ from 'lodash';
import styled from "styled-components";
import { assetHistoryCategories } from '@iso/constants/assetHistoryCategories';
import { assetHistoryTypes } from '@iso/constants/assetHistoryTypes';
import { objectColors } from '@iso/constants/objectColors';
// import PrimaryObjectSelect from "@iso/components/Select/PrimaryObjectSelect";
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { authHeader } from "@iso/lib/helpers/authHeader";

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const AddAssetHistoryModal = (props) => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const currentUser = useSelector((state) => state.user.user);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  
  useEffect(() => {
    if (props.visibleHistory) {
      loadUsers();
      initializeForm();
    }
  }, [props.visibleHistory, currentUser]);

  const loadUsers = async () => {
    try {
      const response = await propertyService.getListUsersOfProperty(activePropertyId);
      if (response && response.users) {
        setUsers(response.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const initializeForm = () => {
    // Set default values for lock out form
    if (currentUser) {
      form.setFieldsValue({
        history: {
          date: moment(),
          expiry_datetime: moment().add(1, 'day'),
          user_id: currentUser.id,
          contact_number: currentUser.mobile_phone || '',
          alarm_minutes: 30,
        }
      });
    }
  };

  const cancelModal = () => {
    props.cancelModal();
    setLoading(false);
    form.resetFields();
    setAttachmentIds([]);
    setUploadFiles([]);
  }

  const handleSubmit = () => {
    setLoading(true);

    form
      .validateFields()
      .then(values => {
        onStore(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
        setLoading(false);
      });
  }

  const onStore = async (values) => {
    const isLockOut = values.history?.activity_category === 'lock_out';
    const isFacility = props.model === 'facilities';
    
    if (isLockOut && isFacility) {
      // Handle lock out submission using facility service
      try {
        const lockOutData = {
          asset_id: props.dataId, // Keep asset_id for backend compatibility
          lock_out: {
            reason: values.history.reason,
            location: values.history.location || '',
            datetime: moment(values.history.date),
            expiry_datetime: moment(values.history.expiry_datetime),
            user_id: values.history.user_id,
            contact_number: values.history.contact_number,
            lock_number: values.history.lock_number,
            lock_colour: values.history.lock_colour || '',
            alarm_minutes: values.history.alarm_minutes || 30,
            manager_id: values.history.manager_id || null,
            attachment_ids: attachmentIds
          }
        };

        const response = await assetService.lockOut(lockOutData);
        
        if (response && (response.code === '0000' || response.code === 200)) {
          cancelModal();
          form.resetFields();
          if (!props.history) {
            props.updateDataAssetHistory(props.dataId, props.model);
          }
          // Trigger asset reload to update state and visual indicators
          if (props.onAssetUpdated) {
            props.onAssetUpdated();
          }
          // Trigger property reload to refresh all assets on map
          if (props.updatePropertyDetail) {
            props.updatePropertyDetail(activePropertyId);
          }
          message.success('Lock out created successfully');
        } else {
          setLoading(false);
          message.error(response?.message || 'Lock out failed');
        }
      } catch (error) {
        setLoading(false);
        console.error('Lock out error:', error);
      }
    } else {
      // Handle regular activity history submission
      const history_data = {
        "history":{
          metadata: values.history,
          action:'create',
          model: props.model,
          object_id: props.dataId,
          property_id: activePropertyId
        }
      }
      
      activityService.storeOrUpdate(history_data).then(res => {
        if(res.code === '0000'){
          cancelModal();
          form.resetFields();
          if (!props.history) {
            props.updateDataAssetHistory(res.history.object_id, res.history.model);
          }
        } else {
          setLoading(false);
        }
      });
    }
  }

  const renderOptionsAssetHistoryCategory = () => {
    let options = [];
    _.forEach(assetHistoryCategories, (asset_history, index) => {
      options.push(
        <Option key={index} value={asset_history.value}>
          <div>{asset_history.label}</div>
        </Option>
      );
    })
    return (
      <Select
        placeholder="Select a category"
        allowClear
        size="large"
      >
        {options}
      </Select>
    );
  }

  const renderOptionsAssetHistoryType = () => {
    let options = [];
    _.forEach(assetHistoryTypes, (type, index) => {
      options.push(
        <Option key={index} value={type.label}>
          <div>{type.label}</div>
        </Option>
      );
    })
    return (
      <Select
        placeholder="Select a type"
        allowClear
        size="large"
      >
        {options}
      </Select>
    );
  }

  const renderOptionObject = (objects) => {
    let options = [];
    _.forEach(objects, (object, index) => {
      options.push(
        <Option key={object.id} value={object.name}>
          {object.name}
        </Option>
      );
    })
    return (
      <Select
        placeholder="Select an object"
        allowClear
        size="large"
      >
        {options}
      </Select>
    );
  }


  const renderUserSelect = () => {
    return (
      <Select placeholder="Select user" allowClear size="large" showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }>
        {users.map((user) => (
          <Option key={user.id} value={user.id}>
            {user.name}
          </Option>
        ))}
      </Select>
    );
  };

  const renderManagerSelect = () => {
    return (
      <Select placeholder="Select manager (optional)" allowClear size="large" showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }>
        {users.map((user) => (
          <Option key={user.id} value={user.id}>
            {user.name}
          </Option>
        ))}
      </Select>
    );
  };

  const renderLockColorOptions = () => {
    let options = [];
    objectColors.forEach((color, index) => {
      options.push(
        <Option key={index} value={color.value}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: color.color,
              border: '1px solid #d9d9d9',
              borderRadius: '2px'
            }}></div>
            <span>{color.label}</span>
          </div>
        </Option>
      );
    });
    return options;
  };

  const onFileChange = (response) => {
    if(response.file && response.file.status === "done"){
      handleUploadAttachmentSuccess(response.file.response.attachment);
    }

    setUploadFiles([...response.fileList]);
  };

  const handleUploadAttachmentSuccess = (attachment) => {
    setAttachmentIds(attachmentIds => [...attachmentIds, attachment.id]);
  }

  const handleRemoveAttachment = (attachment) => {
    const removedAttachmentId = attachment.response?.attachment?.id ?? attachment.id;

    if (removedAttachmentId) {
      attachmentService.destroy(removedAttachmentId);
    }
  }



  return(
    <>
    <AssetModalWrapper
      visible={props.visibleHistory}
      onCancel={cancelModal}
      maskClosable={false}
      title= {<IntlMessages id="propertyPage.mobModal.historyTab.addHistory"/>}
      footer={[
        <Button key="back" onClick={cancelModal} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
        <Button key="submit" className="btn-success" type="primary" onClick={handleSubmit} loading={loading}>
          {<IntlMessages id="propertyPage.modal.save" />}
        </Button>,
      ]}
    >
      <Form
            form={form}
            layout="vertical"
            scrollToFirstError
            id="#1"
          >
          <div className="form-body">
            <Row>
              <Col xs={{span: 24}} md={{span: 11}}>
                <Form.Item
                  name={['history', 'activity_category']}
                  className="isoInputWrapper"
                  label="Category"
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                >
                  {renderOptionsAssetHistoryCategory()}
                </Form.Item>
              </Col>
              
              {/* Hide Type field for lock out category */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues) => {
                  return (prevValues['history']?.activity_category !== form.getFieldValue(['history', 'activity_category']));
                }}
              >
                {({ getFieldValue }) => {
                    if(getFieldValue('history')?.activity_category !== 'lock_out'){
                      return (
                        <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
                          <Form.Item
                            name={['history', 'type']}
                            className="isoInputWrapper"
                            label="Type"
                            rules={[
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]}
                          >
                            {renderOptionsAssetHistoryType()}
                          </Form.Item>
                        </Col>
                      );
                    }
                    return null;
                  }
                }
              </Form.Item>
            </Row>
            <Row>
              <Col xs={{span: 24}} md={{span: 11}}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, _currentValues) => {
                    return (prevValues['history']);
                  }}
                >
                  {({ getFieldValue }) => {
                      if(getFieldValue('history')){
                        if (getFieldValue('history').activity_category === "other"){
                          return (
                              <Form.Item
                              name={['history', 'other_category']}
                              className="isoInputWrapper"
                              label="Other Category"
                              rules={[
                                {
                                  required: true,
                                  message: 'This field is required',
                                  whitespace: true
                                },
                                {
                                  max:255,
                                  message: 'Other Category must be maximum 255 characters.'
                                },
                              ]}
                            >
                              <Input size="large" placeholder="Input other category"/>
                            </Form.Item>
                          );
                        }
                      }
                    }
                  }
                </Form.Item>
              </Col>
              <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, _currentValues) => {
                    return (prevValues['history']);
                  }}
                >
                  {({ getFieldValue }) => {
                      if(getFieldValue('history')){
                        if (getFieldValue('history').type === "Other"){
                          return (
                              <Form.Item
                              name={['history', 'other_type']}
                              className="isoInputWrapper"
                              label="Other Type"
                              rules={[
                                {
                                  required: true,
                                  message: 'This field is required',
                                  whitespace: true
                                },
                                {
                                  max:255,
                                  message: 'Other Type Type must be maximum 255 characters.'
                                },
                              ]}
                            >
                              <Input size="large" placeholder="Input other type"/>
                            </Form.Item>
                          );
                        }
                        if(getFieldValue('history').type === "Hours"){
                          return (
                            <Form.Item
                              name={['history', 'total_hours']}
                              className="isoInputWrapper"
                              label="Total Hours"
                              rules={[
                                {
                                  required: true,
                                  message: 'This field is required',
                                },
                              ]}
                            >
                              <InputNumber style={{ width: '100%' }} size="large" min={1} max={1000} />
                            </Form.Item>
                          );
                        }
                        if(getFieldValue('history').type === "Volume"){
                          return (
                            <Form.Item
                              name={['history', 'total_volume']}
                              className="isoInputWrapper"
                              label="Total Volume"
                              rules={[
                                {
                                  required: true,
                                  message: 'This field is required',
                                },
                              ]}
                            >
                              <InputNumber style={{ width: '100%' }} size="large" min={1} max={1000} />
                            </Form.Item>
                          );
                        }
                      }
                    }
                  }
                </Form.Item>
              </Col>
            </Row>
            {/* Date and Location fields - conditional for non-lock out */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, _currentValues) => {
                return (prevValues['history']?.activity_category !== _currentValues['history']?.activity_category);
              }}
            >
              {({ getFieldValue }) => {
                  if(getFieldValue('history')?.activity_category !== 'lock_out'){
                    return (
                      <Row>
                        <Col xs={{span: 24}} md={{span: 11}}>
                          <Form.Item
                            name={['history', 'date']}
                            className="isoInputWrapper"
                            label="Date"
                            rules={[
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]}
                          >
                            <DatePicker size="large" style={{width: '100%'}}/>
                          </Form.Item>
                        </Col>
                        <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
                          <Form.Item
                            name={['history', 'location']}
                            className="isoInputWrapper"
                            label="Location"
                            rules={[
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]}
                          >
                            {renderOptionObject(props.primaryObjects)}
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  }
                  return null;
                }
              }
            </Form.Item>
            
            {/* Lock Out Form Fields */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, _currentValues) => {
                return (prevValues['history']?.activity_category !== _currentValues['history']?.activity_category);
              }}
            >
              {({ getFieldValue }) => {
                  if(getFieldValue('history')?.activity_category === 'lock_out' && props.model === 'facilities'){
                    return (
                      <>
                        {/* Asset Name (read-only) */}
                        <Row>
                          <Col xs={{span: 24}}>
                            <Form.Item
                              className="isoInputWrapper"
                              label="Asset Name"
                            >
                              <Input 
                                size="large" 
                                value={props.assetName || 'N/A'}
                                disabled
                                style={{ backgroundColor: '#f5f5f5' }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* Date & Time and Expiry Date & Time */}
                        <Row>
                          <Col xs={{span: 24}} md={{span: 11}}>
                            <Form.Item
                              name={['history', 'date']}
                              className="isoInputWrapper"
                              label="Date & Time"
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select date and time',
                                },
                              ]}
                            >
                              <DatePicker 
                                showTime 
                                size="large" 
                                style={{width: '100%'}}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
                            <Form.Item
                              name={['history', 'expiry_datetime']}
                              className="isoInputWrapper"
                              label="Expiry Date & Time"
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select expiry date and time',
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const lockOutDate = getFieldValue(['history', 'date']);
                                    if (!value || !lockOutDate || value.isAfter(lockOutDate)) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Expiry must be after lock out date!'));
                                  },
                                }),
                              ]}
                            >
                              <DatePicker 
                                showTime 
                                size="large" 
                                style={{width: '100%'}}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* Reason and Location */}
                        <Row>
                          <Col xs={{span: 24}} md={{span: 11}}>
                            <Form.Item
                              name={['history', 'reason']}
                              className="isoInputWrapper"
                              label="Reason"
                              rules={[
                                {
                                  required: true,
                                  whitespace: true,
                                  message: 'Please enter a reason',
                                },
                              ]}
                            >
                              <Input 
                                size="large" 
                                placeholder="Enter lock out reason"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
                            <Form.Item
                              name={['history', 'location']}
                              className="isoInputWrapper"
                              label="Location"
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select a location',
                                },
                              ]}
                            >
                              {renderOptionObject(props.primaryObjects)}
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* User and Contact */}
                        <Row>
                          <Col xs={{span: 24}} md={{span: 11}}>
                            <Form.Item
                              name={['history', 'user_id']}
                              className="isoInputWrapper"
                              label="User"
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select a user',
                                },
                              ]}
                            >
                              {renderUserSelect()}
                            </Form.Item>
                          </Col>
                          <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
                            <Form.Item
                              name={['history', 'contact_number']}
                              className="isoInputWrapper"
                              label="Contact Number"
                              rules={[
                                {
                                  required: true,
                                  whitespace: true,
                                  message: 'Please enter contact number',
                                },
                              ]}
                            >
                              <Input 
                                size="large" 
                                placeholder="Contact number"
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* Lock Number and Color */}
                        <Row>
                          <Col xs={{span: 24}} md={{span: 11}}>
                            <Form.Item
                              name={['history', 'lock_number']}
                              className="isoInputWrapper"
                              label="Lock Number"
                              rules={[
                                {
                                  required: true,
                                  whitespace: true,
                                  message: 'Please enter lock number',
                                },
                              ]}
                            >
                              <Input 
                                size="large" 
                                placeholder="Lock identification number"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
                            <Form.Item
                              name={['history', 'lock_colour']}
                              className="isoInputWrapper"
                              label="Lock Color"
                            >
                              <Select
                                size="large"
                                placeholder="Select lock color"
                                allowClear
                              >
                                {renderLockColorOptions()}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* Alarm and Manager */}
                        <Row>
                          <Col xs={{span: 24}} md={{span: 11}}>
                            <Form.Item
                              name={['history', 'alarm_minutes']}
                              className="isoInputWrapper"
                              label="Alarm Before Expiry (minutes)"
                              initialValue={30}
                            >
                              <InputNumber 
                                size="large" 
                                style={{ width: '100%' }} 
                                min={1} 
                                max={10080}
                                placeholder="Minutes before expiry"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
                            <Form.Item
                              name={['history', 'manager_id']}
                              className="isoInputWrapper"
                              label="Manager"
                            >
                              {renderManagerSelect()}
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* Photos */}
                        <Row>
                          <Col xs={{span: 24}}>
                            <Form.Item
                              className="isoInputWrapper"
                              label="Attach photos, videos, documents"
                            >
                              <Upload
                                action={apiEndpoint.attachments}
                                headers={authHeader(true)}
                                listType="picture"
                                name="Attachment[file]"
                                fileList={uploadFiles}
                                multiple={true}
                                onChange={onFileChange}
                                data={{ type: 'lock_out', storage: 'filesystem' }}
                                onRemove={handleRemoveAttachment}
                              >
                                <Button icon={<UploadOutlined />} size="large">
                                  Upload
                                </Button>
                              </Upload>
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    );
                  }
                  return null;
                }
              }
            </Form.Item>
            
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, _currentValues) => {
                return (prevValues['history']?.activity_category !== _currentValues['history']?.activity_category);
              }}
            >
              {({ getFieldValue }) => {
                  if(getFieldValue('history')?.activity_category !== 'lock_out' && props.model === 'facilities'){
                    return (
                      <Form.Item
                        name={['history', 'description']}
                        className="isoInputWrapper"
                        label="Description"
                      >
                        <Input.TextArea />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
            </Form.Item>
          </div>
      </Form>
    </AssetModalWrapper>
    </>
  )
}

export default AddAssetHistoryModal;
